import { ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import { Spinner } from "../ui/Spinner.jsx";
import { EmptyState } from "../ui/EmptyState.jsx";
import { cn } from "../../lib/cn.js";

/**
 * Generic data table for list views.
 *
 *   <DataTable
 *     columns={[
 *       { key: 'name', label: 'Name', sortable: true, render: (row) => <strong>{row.name}</strong> },
 *       { key: 'status', label: 'Status', render: (row) => <Badge>{row.status}</Badge> },
 *     ]}
 *     rows={items}
 *     loading={isLoading}
 *     sort={{ field: 'name', dir: 'asc' }}
 *     onSortChange={(field, dir) => setSort({ field, dir })}
 *     onRowClick={(row) => navigate(`/designs/${row._id}`)}
 *     actions={(row) => <RowActions row={row} />}
 *     empty={{ title: "No designs yet", description: "..." }}
 *   />
 *
 * Pagination + filter bar are sibling components — keep them separate so
 * inline-edit pages can compose them differently.
 */
export function DataTable({
  columns,
  rows = [],
  loading = false,
  sort,
  onSortChange,
  onRowClick,
  rowKey = (r) => r._id ?? r.id,
  actions,
  empty,
  className,
}) {
  const handleSort = (col) => {
    if (!col.sortable || !onSortChange) return;
    const isCurrent = sort?.field === col.key;
    const nextDir = isCurrent && sort.dir === "asc" ? "desc" : "asc";
    onSortChange(col.key, nextDir);
  };

  if (!loading && rows.length === 0 && empty) {
    return <EmptyState {...empty} />;
  }

  return (
    <div className={cn("overflow-hidden rounded-xl border border-border-subtle", className)}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-bg-elevated-2 text-xs uppercase tracking-wider text-text-muted">
            <tr>
              {columns.map((col) => {
                const isSorted = sort?.field === col.key;
                const SortIcon = !col.sortable
                  ? null
                  : !isSorted
                    ? ChevronsUpDown
                    : sort.dir === "asc"
                      ? ChevronUp
                      : ChevronDown;
                return (
                  <th
                    key={col.key}
                    onClick={() => handleSort(col)}
                    className={cn(
                      "border-b border-border-subtle px-4 py-3 text-left font-semibold",
                      col.sortable && "cursor-pointer hover:text-text-primary",
                      col.className
                    )}
                  >
                    <div className="flex items-center gap-1.5">
                      {col.label}
                      {SortIcon && (
                        <SortIcon
                          className={cn(
                            "h-3.5 w-3.5",
                            isSorted ? "text-accent" : "text-text-muted"
                          )}
                        />
                      )}
                    </div>
                  </th>
                );
              })}
              {actions && (
                <th className="border-b border-border-subtle px-4 py-3 text-right font-semibold">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (actions ? 1 : 0)} className="py-12 text-center">
                  <Spinner size="md" />
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={rowKey(row)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={cn(
                    "border-b border-border-subtle bg-bg-elevated transition-colors last:border-b-0",
                    onRowClick && "cursor-pointer hover:bg-bg-elevated-2"
                  )}
                >
                  {columns.map((col) => (
                    <td key={col.key} className={cn("px-4 py-3", col.cellClassName)}>
                      {col.render ? col.render(row) : (row[col.key] ?? "—")}
                    </td>
                  ))}
                  {actions && (
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      {actions(row)}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
